// @ts-nocheck
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useContractAnalysis } from "src/hooks/contracts/useContractAnalysis";
import type { ContractAnalysisProps, GetContractResponseContent } from "src/types/contract.types";
import { TopNavigation } from "./components/top-navigation";
import { ContractAnalysisLoadingView } from "./components/loading";
import { ActivitySidebar } from "./components/activity-sidebar";
import { ActivitySidebarProvider, useSidebarContext } from "./contexts/activity-sidebar-context";
import { ContractEditor } from "./contract-editor";
import { Panel, Group, Separator } from "react-resizable-panels";
import { DealProgressTimeline } from "src/views/deals/components/DealPage/DealProgressTimeline";
import { useDealProgressStream } from "src/hooks/deal/useDealProgressStream";

interface ContractAnalysisComponentProps extends ContractAnalysisProps {
  isDemo?: boolean;
  hideNavigation?: boolean;
}

type ViewMode = "edit" | "viewer";

function ContractAnalysisContent({
  contract,
  hideNavigation,
  viewMode,
  onViewModeChange,
  dealAnalysisId,
  analysisStatus,
  isDemo,
}: {
  contract: GetContractResponseContent | null;
  hideNavigation: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  dealAnalysisId: string | null;
  analysisStatus: string | null;
  isDemo: boolean;
}) {
  const { sidebarOpen, openSidebar, setSidebarTab } = useSidebarContext();
  const [iqHighlightedTerm, setIqHighlightedTerm] = useState<string | null>(null)
  const [pageToView, setPageToView] = useState<number | undefined>(undefined)
  const sourceTaggedText = contract?.contractExtraction?.contractTexts?.taggedText?.text ?? ""
  const [taggedContractText, setTaggedContractText] = useState(sourceTaggedText)

  useEffect(() => {
    setTaggedContractText(sourceTaggedText)
  }, [contract?.contractId, sourceTaggedText])

  // A citation "Source: page N" click drives the left pane to the PDF
  // viewer at that page. Re-clicking the same page must re-fire the jump,
  // so bump a nonce alongside the page (PDFReader keys its jump effect off
  // the prop value — identical values are a no-op without the nonce).
  const handleJumpToPage = useCallback((page: number) => {
    if (viewMode !== "viewer") {
      onViewModeChange("viewer")
    }
    setPageToView(undefined)
    // Defer so PDFReader sees a value change even when re-jumping the same
    // page; the microtask gap turns same-page re-clicks into a real update.
    queueMicrotask(() => setPageToView(page))
  }, [viewMode, onViewModeChange])

  const handleViewerTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
    openSidebar()
    setSidebarTab("citations")
  }, [openSidebar, setSidebarTab])

  const handleCitationTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
  }, [])

  const handleTaggedContractTextChange = useCallback((nextText: string) => {
    setTaggedContractText(nextText)
  }, [])

  const handleIqTermSelect = useCallback((termWithInstance: string) => {
    setIqHighlightedTerm(termWithInstance)
    if (viewMode === "edit") {
      onViewModeChange("viewer")
    }
  }, [onViewModeChange, viewMode])

  // Show the live SSE timeline whenever the engine hasn't finished writing
  // its analysisData yet. We treat 'complete' as "hide" — the EQ + IQ
  // panels carry the meaningful state from there. Anything else (pending,
  // running, failed, unknown — including "we have a dealAnalysisId from
  // navigation state but no /getContract response yet") keeps the timeline
  // visible so the user has live signal that processing is happening.
  const showProgressTimeline =
    !!dealAnalysisId && analysisStatus !== "complete";

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {!hideNavigation && (
        <TopNavigation
          contract={contract}
        />
      )}

      {showProgressTimeline && (
        <div className="px-4 py-2 border-b border-border bg-neutral-50 dark:bg-neutral-900/40">
          <DealProgressTimeline
            dealAnalysisId={dealAnalysisId}
            initialStatus={analysisStatus ?? undefined}
            className="max-w-3xl mx-auto"
          />
        </div>
      )}

      <Group orientation="horizontal" className="flex-1 min-h-0">
        <Panel defaultSize={70} minSize={40}>
          <div className={sidebarOpen ? "h-full overflow-hidden border-r" : "h-full overflow-hidden"}>
            <ContractEditor
              mode={viewMode}
              onModeChange={onViewModeChange}
              contract={contract}
              highlightedTerm={iqHighlightedTerm}
              onHighlightedTermSelect={handleViewerTermSelect}
              taggedContractText={taggedContractText}
              onTaggedContractTextChange={handleTaggedContractTextChange}
              pageToView={pageToView}
            />
          </div>
        </Panel>
        {sidebarOpen && (
          <>
            <Separator className="w-1 bg-border/50 hover:bg-border transition-colors cursor-col-resize" />
            <Panel defaultSize={50} minSize={20}>
              <div className="sticky top-0 h-[calc(100vh-4rem)]">
                <ActivitySidebar
                  contract={contract ?? undefined}
                  onIqTermSelect={handleIqTermSelect}
                  highlightedTerm={iqHighlightedTerm}
                  onCitationTermSelect={handleCitationTermSelect}
                  taggedContractText={taggedContractText}
                  isDemo={isDemo}
                  onJumpToPage={handleJumpToPage}
                />
              </div>
            </Panel>
          </>
        )}
      </Group>
    </div>
  );
}

const ContractAnalysis = ({ providedContract, isDemo = false, hideNavigation = false }: ContractAnalysisComponentProps) => {
  const {
    contract,
    isLoading,
    refreshContract,
  } = useContractAnalysis(providedContract, isDemo);

  // Pull the dealAnalysisId from two sources, in order of trust:
  //   1. router state — set by NewDealPage.tsx right after the engine push
  //      so the timeline can mount before /getContract has even returned.
  //   2. the contract response itself — once /getContract resolves, the
  //      adapter's `dealAnalysisId` field carries the canonical value.
  // Demo mode skips both — there's no engine running for demo contracts.
  const location = useLocation();
  const stateAnalysisId =
    (location.state as { dealAnalysisId?: string } | null)?.dealAnalysisId ?? null;
  const responseAnalysisId =
    (contract as (GetContractResponseContent & { dealAnalysisId?: string }) | null)
      ?.dealAnalysisId ?? null;
  const responseStatus =
    (contract as (GetContractResponseContent & { analysisStatus?: string }) | null)
      ?.analysisStatus ?? null;
  const dealAnalysisId = isDemo ? null : (responseAnalysisId ?? stateAnalysisId);

  // Subscribe to engine progress so we can refetch /getContract the moment
  // the analysisData JSONB is populated. Without this the page would
  // happily render the empty-envelope state forever, since SWR has no
  // reason to revalidate on its own.
  // Seed the SSE hook with the snapshot status from /getContract so the
  // strip-header pill renders the correct value at t=0 instead of
  // flashing "Running" for ~15s while the SSE replay catches up.
  const liveStatus = useLiveAnalysisStatus(
    dealAnalysisId,
    refreshContract,
    responseStatus,
  );
  const effectiveStatus = liveStatus ?? responseStatus;

  const [viewMode, setViewMode] = useState<ViewMode>("viewer");

  if (isLoading) {
    return <ContractAnalysisLoadingView />
  }

  return (
    <ActivitySidebarProvider>
      <ContractAnalysisContent
        contract={contract}
        hideNavigation={hideNavigation}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        dealAnalysisId={dealAnalysisId}
        analysisStatus={effectiveStatus}
        isDemo={isDemo}
      />
    </ActivitySidebarProvider>
  );
}

/**
 * Subscribe to the engine SSE stream and trigger an SWR refresh of the
 * contract response on the leading edge of `status === "complete"`. The
 * second EventSource is intentional — DealProgressTimeline owns its own
 * for the visual stream, this one drives data revalidation. Both close
 * automatically once the engine finishes, so the duplicate is short-lived.
 *
 * Returns the live derived status so the parent can render the timeline
 * without waiting on /getContract to revalidate.
 */
function useLiveAnalysisStatus(
  dealAnalysisId: string | null,
  refresh: () => Promise<void>,
  initialStatus: string | null,
): string | null {
  const { status } = useDealProgressStream(
    dealAnalysisId,
    initialStatus ?? undefined,
  );
  const refreshedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!dealAnalysisId) return;
    if (status !== "complete") return;
    if (refreshedRef.current === dealAnalysisId) return;
    refreshedRef.current = dealAnalysisId;
    void refresh();
  }, [dealAnalysisId, status, refresh]);

  return dealAnalysisId ? status : null;
}

export default ContractAnalysis;
